import { Button, CheckBox } from '@iwoca/orion';
import { RootState } from '@iwProvider/configureStore';
import { useStripe } from '@stripe/react-stripe-js';
import { Stripe, StripeCardNumberElement } from '@stripe/stripe-js';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import { BillingPostcodeField } from './formFields/BillingPostcodeField';
import { CardholderNameField } from './formFields/CardholderNameField';
import { CardNumberField } from './formFields/CardNumberField';
import { CVVField } from './formFields/CVVField';
import { ExpiryDateField } from './formFields/ExpiryDateField';
import {
    HANDLE_STRIPE_FIELD_CHANGE,
    SAVE_CARD_NUMBER_ELEMENT
} from '../../modals/addPaymentMethodModal/AddPaymentMethodModal.actions';
import styles from '../LinkDebitCard.module.css';

export type TFormValues = {
    billingPostcode: string;
    cardholderName: string;
    repaymentRole: boolean;
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
    saveCardNumberElement: (cardNumberElement: StripeCardNumberElement) =>
        dispatch({
            type: SAVE_CARD_NUMBER_ELEMENT,
            cardNumberElement
        }),
    handleFieldChange: (field: string, response: any) =>
        dispatch({
            type: HANDLE_STRIPE_FIELD_CHANGE,
            field: field,
            response: response
        })
});

const mapStateToProps = (state: RootState) => ({
    ...state.addPaymentMethodModal
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

type OwnProps = {
    linkingDebitCard?: boolean;
    showDefaultPaymentMethodCheckbox?: boolean;
    useSectionGridLayout?: boolean;
    onSuccess: (
        stripe: Stripe,
        repaymentRole: string | null,
        billingPostcode: string,
        cardholderName: string
    ) => void;
};

const StripeLinkDebitCardFormBase = ({
    invalidCardType,
    invalidCard,
    invalidNumber,
    invalidCVV,
    invalidExpiryDate,
    linkingDebitCard,
    registerCardStatus,
    onSuccess,
    saveCardNumberElement,
    handleFieldChange,
    showDefaultPaymentMethodCheckbox = false,
    useSectionGridLayout = false
}: OwnProps & PropsFromRedux) => {
    const {
        register,
        handleSubmit,
        formState: { errors }
    } = useForm<TFormValues>({
        defaultValues: {
            billingPostcode: '',
            cardholderName: '',
            repaymentRole: true
        }
    });

    const intl = useIntl();

    const handleChange = (field: string) => (response: any) => {
        handleFieldChange(field, response);
    };

    const stripe = useStripe();

    const onSubmit = ({
        billingPostcode,
        cardholderName,
        repaymentRole
    }: TFormValues) => {
        onSuccess(
            stripe!,
            repaymentRole ? 'primary' : null,
            billingPostcode,
            cardholderName
        );
    };

    return (
        <form
            data-testid="link-debit-card-modal"
            className={styles.LinkDebitCardForm}
            id="payment-form"
            onSubmit={handleSubmit(onSubmit)}
        >
            <div
                className={
                    useSectionGridLayout
                        ? 'grid gap-l pt-xl min-m:grid-cols-3'
                        : styles.topRow
                }
            >
                <CardNumberField
                    onReady={(element) => saveCardNumberElement(element)}
                    error={{
                        invalidNumber,
                        invalidCard,
                        invalidCardType
                    }}
                    onChange={handleChange('cardNumber')}
                />
                <ExpiryDateField
                    error={invalidExpiryDate}
                    onChange={handleChange('expiryDate')}
                />
                <CVVField error={invalidCVV} onChange={handleChange('CVV')} />
                <BillingPostcodeField
                    formRegister={register}
                    error={errors.billingPostcode}
                />
                <CardholderNameField
                    formRegister={register}
                    error={errors.cardholderName}
                />
            </div>

            {showDefaultPaymentMethodCheckbox && (
                <div className="mb-2xl">
                    <CheckBox
                        label={intl.formatMessage({
                            id: '_.link_debit_card.repayment_role_label',
                            defaultMessage:
                                'Use as default payment method for automatic repayments'
                        })}
                        {...register('repaymentRole')}
                    />
                </div>
            )}

            {registerCardStatus ? (
                <FormattedMessage
                    id="_.link_debit_card.successful_link"
                    defaultMessage="Thanks! You've successfully linked your card! Continue to the next step"
                />
            ) : null}

            <div className="mt-xl flex w-full flex-col items-center gap-m">
                <Button
                    type="submit"
                    className="w-full max-w-[300px]"
                    loading={linkingDebitCard}
                >
                    <FormattedMessage
                        id="_.link_debit_card.link_card"
                        defaultMessage="Add your card"
                    />
                </Button>
            </div>
        </form>
    );
};

export const StripeLinkDebitCardForm = connect(
    mapStateToProps,
    mapDispatchToProps
)(StripeLinkDebitCardFormBase);
